<script lang="ts">import { menuAutoCollapsed } from "../state";
import { Background } from "@infinitro/background";
import { cfgEventThemeCdnPath, isEventAssetEnabled, isEventThemeActive, stateUi, toTokenIconCdn } from "@infinitro/config";
import { currencyInfo, currencyPrices, isCrypto, startCurrencyPriceFeed } from "@infinitro/global";
import { Currency, FiatCurrency } from "@infinitro/global/wallet";
import { CurrencyIcon, DropdownArrowIcon } from "@infinitro/icons";
import { DotsLoader } from "@infinitro/loading";
import { onMount } from "svelte";
import { SvelteSet } from "svelte/reactivity";
let useExpandedRankPresentation = $derived($stateUi.layout?.useExpandedRankPresentation ?? false);
let useEmbeddedMenuBackdrop = $derived($stateUi.footer?.useEmbeddedShellDecor ?? false);
let isMenuCollapsed = $state(false);
let dropdownOpen = $state(false);
// Only the selected *currency* is stateful (a primitive, safe to write from an effect/handler);
// the token object itself is always looked up fresh from `items` so its price stays live.
let selectedCurrency = $state(undefined);
// The CDN icon URL is built from the currency code with no manifest of what actually
// exists there, so a currency the backend adds before its icon is uploaded would otherwise
// render as a broken image. We probe each icon and drop the currency from the list until
// it loads successfully. A failed load gets one retry (a transient network blip shouldn't
// permanently hide a valid currency) capped at MAX_ICON_LOAD_ATTEMPTS total requests per
// currency, ever, for the life of the component — never an unbounded/looping retry.
const MAX_ICON_LOAD_ATTEMPTS = 2;
const ICON_RETRY_DELAY_MS = 3e3;
const brokenIconCurrencies = new SvelteSet();
const checkedIconCurrencies = new SvelteSet();
function probeIcon(currency, attempt) {
	const probe = new Image();
	probe.onerror = () => {
		if (attempt < MAX_ICON_LOAD_ATTEMPTS) {
			setTimeout(() => probeIcon(currency, attempt + 1), ICON_RETRY_DELAY_MS);
		} else {
			brokenIconCurrencies.add(currency);
		}
	};
	probe.src = toTokenIconCdn(currency);
}
$effect(() => {
	for (const { currency } of $currencyPrices ?? []) {
		if (checkedIconCurrencies.has(currency)) continue;
		checkedIconCurrencies.add(currency);
		probeIcon(currency, 1);
	}
});
let items = $derived.by(() => {
	const infoRows = $currencyInfo;
	const prices = $currencyPrices;
	if (!prices) return [];
	return prices.map((a) => {
		const row = infoRows.find((b) => b.currency === a.currency);
		try {
			return {
				...a,
				color: row?.color,
				price: parseFloat(a.price)
			};
		} catch {
			return a;
		}
	}).filter((a) => isCrypto(a.currency)).filter((a) => a.price > 0 && a.price != 1).filter((a) => !brokenIconCurrencies.has(a.currency)).sort((a, b) => a.price > b.price ? -1 : 1);
});
let hasPrices = $derived(items.length > 0);
// Keeps the previous selection if it's still in the list (with a fresh price), otherwise
// falls back to the top item.
let selectedToken = $derived.by(() => {
	if (items.length === 0) return undefined;
	if (selectedCurrency) {
		return items.find((a) => a.currency === selectedCurrency) ?? items[0];
	}
	return items[0];
});
const toggleDropdown = () => {
	if (!hasPrices) return;
	dropdownOpen = !dropdownOpen;
};
const selectToken = (token) => {
	selectedCurrency = token.currency;
	dropdownOpen = false;
};
menuAutoCollapsed.subscribe((value) => {
	dropdownOpen = false;
	isMenuCollapsed = value;
});
let borderColor = $derived(useExpandedRankPresentation ? "var(--color-tertiary-500)" : "rgba(44, 55, 99, 1)");
let dropdownElement = $state(undefined);
onMount(() => {
	startCurrencyPriceFeed();
	// Set event theme button addon background if active
	if (dropdownElement && isEventAssetEnabled("buttonAddon")) {
		const buttonAddonPath = cfgEventThemeCdnPath("buttonaddon.png");
		if (buttonAddonPath) {
			dropdownElement.style.setProperty("--event-buttonaddon-bg", `url('${buttonAddonPath}')`);
		}
	}
});
</script>

<!-- svelte-ignore a11y_no_static_element_interactions -->
<div
	class={`custom-dropdown ${isMenuCollapsed ? 'custom-dropdown-collapsed' : ''}`}
	class:event-theme={isEventThemeActive()}
	bind:this={dropdownElement}
>
	{#if !isMenuCollapsed}
		<!-- svelte-ignore a11y_click_events_have_key_events -->
		<div
			class="dropdown-toggle"
			class:dropdown-toggle--extended-rank={useExpandedRankPresentation}
			onclick={toggleDropdown}
			style="background-image: linear-gradient(180deg, var(--color-background-900) 0%, var(--color-background-100) 100%), linear-gradient(to right, {selectedToken?.color ??
				'#FFFFFF'} 0%, {borderColor} 11%, {borderColor} 100%);"
		>
			<div class="dropdown-content {isMenuCollapsed && 'dropdown-content-collapsed'}">
				<div class="d-flex crypto-price">
					{#if selectedToken?.currency}
						<CurrencyIcon currency={selectedToken.currency as CurrencyIconProp} size="23" />
					{/if}
					<span class="crypto-color" style="color: {selectedToken?.color ?? '#FFFFFF'}">
						{selectedToken?.currency?.toUpperCase() ?? ''}
					</span>
					{#if hasPrices}
						<span>{selectedToken?.price ? `$${selectedToken?.price}` : 'N/A'}</span>
					{:else}
						<span class="price-loader-wrapper">
							<DotsLoader color="var(--color-neutral-100)" />
						</span>
					{/if}
					<!-- <span class="percentage-positive d-flex">
                        +0.50%
                        <IncreasedIcon />
                    </span> -->
				</div>
				<div class="arrow-container {dropdownOpen ? 'rotated' : ''}">
					<DropdownArrowIcon />
				</div>
			</div>
		</div>
	{:else}
		<!-- svelte-ignore a11y_click_events_have_key_events -->
		<div
			class="dropdown-toggle dropdown-toggle-collapsed"
			onclick={toggleDropdown}
			style="background-image: linear-gradient(180deg, var(--color-background-900) 0%, var(--color-background-100) 100%), linear-gradient(to right, {selectedToken?.color ??
				'#FFFFFF'} 0%, rgba(44, 55, 99, 1) 11%, rgba(44, 55, 99, 1) 100%);"
		>
			<div class="dropdown-content {isMenuCollapsed && 'dropdown-content-collapsed'}">
				<div class="d-flex crypto-price crypto-price-collapsed">
					{#if selectedToken?.currency}
						<CurrencyIcon currency={selectedToken.currency as CurrencyIconProp} size="23" />
					{/if}
				</div>
			</div>
		</div>
	{/if}

	<div
		class:dropdown-menu--extended-rank={useExpandedRankPresentation}
		class={`dropdown-menu ${isMenuCollapsed ? 'dropdown-menu-collapsed' : ''}`}
		class:expanded={dropdownOpen && hasPrices}
	>
		{#if useEmbeddedMenuBackdrop}
			<Background embedSurface medium={true} rounded={true} rounded2xl={true} zIndex="0" />
		{/if}
		{#if hasPrices}
			{#each items as item, index (item.currency)}
				<!-- svelte-ignore a11y_click_events_have_key_events -->
				<!-- svelte-ignore a11y_no_static_element_interactions -->
				<div
					class={`dropdown-item ${isMenuCollapsed ? 'dropdown-item-collapsed' : ''} ${index === 0 ? 'first-item' : ''} ${selectedToken?.currency === item.currency ? 'selected' : ''}`}
					onclick={() => selectToken(item)}
				>
					<CurrencyIcon currency={item.currency as CurrencyIconProp} size="23" />
					{#if !isMenuCollapsed}
						<span class="crypto-color" style="color: {item?.color ?? '#FFFFFF'}">
							{item?.currency.toUpperCase()}
						</span>
						<span>{item?.price ? `$${item.price}` : 'N/A'}</span>
					{/if}
				</div>
			{/each}
		{/if}
	</div>
</div>

<style>
	.custom-dropdown {
		position: relative;
		width: 100%;
		font-weight: 700;
		overflow: visible;
}

	.custom-dropdown.event-theme::after {
		content: '';
		position: absolute;
		top: -8px;
		right: 0px;
		width: 36px;
		height: 36px;
		background-image: var(--event-buttonaddon-bg);
		background-size: contain;
		background-position: center;
		background-repeat: no-repeat;
		pointer-events: none;
		z-index: 50;
	}

	.custom-dropdown-collapsed {
		width: 100%;
}

	.dropdown-toggle {
		position: relative;
		z-index: 40;
		display: block;
		height: 2.5rem;
		width: 100%;
		cursor: pointer;
		border-radius: 16px;
		text-transform: uppercase;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
		display: flex;
		align-items: center;
		justify-content: space-between;
		background-origin: border-box;
		background-clip: content-box, border-box;
		border: double 1px transparent;
}

	.dropdown-toggle--extended-rank {
		border-radius: 12px;
}

	.dropdown-toggle-collapsed {
		display: flex;
		height: 40px;
		width: 100%;
		align-items: center;
}

	.dropdown-content {
		display: flex;
		width: 100%;
		align-items: center;
		justify-content: space-between;
		gap: 0.25rem;
		padding-top: 1rem;
		padding-bottom: 1rem;
		padding-left: 0.625rem;
		padding-right: 0.625rem;
}

	.dropdown-content-collapsed {
		width: auto;
		padding: 0px;
		padding-left: 0.625rem;
		padding-right: 0.625rem;
}

	.dropdown-menu {
		position: relative;
		margin-top: -1.75rem;
		width: 100%;
		overflow: hidden;
		border-radius: 16px;
		border-width: 1px;
		border-color: var(--color-neutral-900);
		background-color: transparent;
		padding-top: 1.25rem;
		transition-property: max-height;
		transition-duration: 500ms;
		transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
		max-height: 0;
}

	.dropdown-menu--extended-rank {
		border-color: var(--color-accent-700);
		background-color: var(--color-background-100);
		border-radius: 12px;
}

	.dropdown-menu-collapsed {
		width: 100%;
}

	.dropdown-menu.expanded {
		max-height: 100%;
	}

	.dropdown-item {
		position: relative;
		cursor: pointer;
		padding: 0.625rem;
		padding-top: 0.375rem;
		padding-bottom: 0.375rem;
		font-size: 14px;
		text-transform: uppercase;
		--tw-text-opacity: 1;
		color: rgb(156 163 175 / var(--tw-text-opacity, 1));
		display: flex;
		align-items: center;
		gap: 0.5rem;
}

	.dropdown-item:hover {
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
}

	.dropdown-item-collapsed {
		display: flex;
		width: 100%;
		align-items: center;
		justify-content: center;
		padding-left: 0px;
		padding-right: 0px;
}

	.dropdown-item.selected {
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
}

	.first-item {
		padding-top: 1rem;
}

	.d-flex {
		display: flex;
		align-items: center;
		gap: 0.25rem;
}

	.crypto-price {
		font-size: 14px;
}

	.crypto-price-collapsed {
		display: flex;
		flex-direction: column;
}

	.percentage-positive {
		color: var(--color-success-500);
		font-family: var(--font-main);
}

	.arrow-container {
		transition-property: transform;
		transition-duration: 300ms;
		transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
}

	.arrow-container.rotated {
		transform: rotate(180deg);
	}

	.price-loader-wrapper {
		display: inline-flex;
		align-items: center;
}
</style>
